/**
 * The shape of a handful of recent readings (T-907).
 *
 * Normalising a few numbers into a unit box is arithmetic, so it lives here and
 * only the drawing is platform work — the native app will use a different
 * renderer and needs the same points.
 *
 * Two cases decide whether this is honest, and both produce a horizontal line
 * at the bottom of the box if written carelessly:
 *
 * - **Nothing recorded** must draw *nothing*. `Math.min(...[])` is `Infinity`,
 *   and the usual guard against it is a zero default, which puts a flat line
 *   along the axis. That reads as "recorded, always zero" — a claim about data
 *   the user never entered, on a screen whose whole job is saying what they did
 *   record.
 * - **Every reading identical** is a real flat line and must draw one. The
 *   range is zero, so the naive normalisation divides by it; the fix is to
 *   centre the line rather than to fall back to the bottom.
 */

/** Points in a unit box: x and y both 0–1, with y up. */
export interface SparkPoint {
  readonly x: number
  readonly y: number
}

/** How many readings a row's sparkline shows. */
export const SPARKLINE_POINTS = 7

/**
 * `values` normalised into a unit box, or `null` when there is nothing to draw.
 *
 * Scaled to its own values rather than to the track's declared bounds: at this
 * size the shape is suggestive rather than readable, and a 1–10 track sitting
 * between 6 and 7 all week is a flat line against its bounds and a visible
 * wobble against itself. The wobble is the information.
 *
 * One reading is `null` too. A single point is not a shape, and drawing it as a
 * dot in the middle of the box implies a midpoint that nothing measured.
 */
export function sparkline(values: readonly number[]): readonly SparkPoint[] | null {
  if (values.length < 2) {
    return null
  }

  let min = values[0] ?? 0
  let max = min
  for (const value of values) {
    if (!Number.isFinite(value)) {
      return null
    }
    min = Math.min(min, value)
    max = Math.max(max, value)
  }

  const span = max - min
  const lastIndex = values.length - 1

  return values.map((value, index) => ({
    x: index / lastIndex,
    // A flat run centres rather than bottoming out: 0.5 says "steady", 0 says
    // "at the floor", and only one of those is true of a week of sevens.
    y: span === 0 ? 0.5 : (value - min) / span,
  }))
}
